import * as React from 'react';
import { useContext, useEffect } from 'react';
import * as TFF from '@tff/types/TFF';
import { RetrieveFlightResponse, TffBookingSource } from '@tff/types/TFF';
import { ISourceConfig } from '../models';
import { LoadingContext } from '../contexts/LoadingContextProvider';
import { AxiosResponse } from 'axios';
import { useQueryClient } from 'react-query';
import { keycloak } from '../keycloak';
import { CreateJourneyExtras } from '../util/create-journey-extras';
import { apigeeEndpoint } from '../apis';
import { MarketType } from '../types/administrationTypes';
import { AwsLambdaErrorResponse } from '../types/awsLambdaTypes';
import { BENE_CASES, GAS_CASES } from '../values/market-values';
import { isNdcBooking, isNskBooking } from '../util/bookingSources';

export type RetrieveBookingProps = {
  recordLocator?: string;
  sourceConfig?: ISourceConfig;
};

export type TypeOfStatus = 'IN_FETCHING' | 'FETCHED' | 'FAILURE';
const GAS_AGENCY_ID = '9112019';
const UNKNOWN_ERROR_MESSAGE = 'Unknown error occurred';

export type RetrieveBookingResponse = {
  fetchedBooking?: TFF.FlightDetails;
  status?: TypeOfStatus;
  error?: string | RetrievePermissionError | undefined;
  retrieveLoading?: boolean;
  resetRetrieveBookingState: () => void;
  market: MarketType;
};

export type RetrievePermissionError = {
  permissionError: string;
  officeId?: string;
  agencyId?: string;
  bookingSource?: TffBookingSource;
  market?: string;
};

export const mapOfficeIdWithRole = (officeId: string): MarketType | undefined => {
  /** CR cases **/
  if (GAS_CASES.some(prefix => officeId.startsWith(prefix))) {
    return 'M_GAS';
  } else if (BENE_CASES.some(prefix => officeId.startsWith(prefix))) {
    return 'M_BENE';
  } else {
    switch (officeId) {
      /** UK/I cases **/
      case 'LONU128XK':
        return 'M_UK';
      case 'DUBIE38XZ':
        return 'M_IE';
      /** Nordics cases **/
      case 'STOS128FD':
        return 'M_SE';
      case 'OSLS128FD':
        return 'M_NO';
      case 'HELS128FD':
        return 'M_FI';
      case 'CPHS128FD':
        return 'M_DK';
    }
  }
};

export const checkMarketAccess = (
  bookingSource: TffBookingSource,
  officeId?: string,
  agencyId?: string,
  payments?: TFF.Payment[],
): RetrievePermissionError | { market: MarketType } => {
  let requiredRole: MarketType;

  if (isNdcBooking(bookingSource)) {
    if (!officeId) {
      return { permissionError: 'no_office_id' };
    }
    requiredRole = mapOfficeIdWithRole(officeId);
    if (!requiredRole) {
      return { permissionError: 'no_valid_officeId_rule_found', officeId };
    }
  } else if (isNskBooking(bookingSource)) {
    if (!agencyId) {
      return { permissionError: 'no_agency_id' };
    }
    if (agencyId === GAS_AGENCY_ID) {
      requiredRole = 'M_GAS';
    } else {
      /** For NSK manual bookings the agencyId comes in payments **/
      if (payments) {
        const gasPayment = payments.find(p => Object.values(p).includes(GAS_AGENCY_ID));
        requiredRole = gasPayment ? 'M_GAS' : 'M_BENE';
      } else {
        return { permissionError: 'no_valid_agencyId_rule_found', agencyId };
      }
    }
  } else {
    return { permissionError: 'no_valid_source_access_rule_found', bookingSource };
  }

  if (!keycloak.hasRealmRole(requiredRole)) {
    return { permissionError: 'no_market_permission', market: requiredRole.split('_')[1] };
  } else {
    return { market: requiredRole };
  }
};

const checkSourceAccess = (
  bookingSource?: TffBookingSource,
): RetrievePermissionError | { bookingSource: TffBookingSource } => {
  if (!bookingSource) {
    return { permissionError: 'no_booking_source' };
  }

  let requiredRole: string;
  if (isNdcBooking(bookingSource)) {
    requiredRole = 'S_NDC';
  } else if (isNskBooking(bookingSource)) {
    requiredRole = 'S_NSK';
  } else {
    return { permissionError: 'no_valid_source_access_rule_found', bookingSource };
  }

  if (!requiredRole || !keycloak.hasRealmRole(requiredRole)) {
    return { permissionError: 'no_source_permission' };
  } else {
    return { bookingSource };
  }
};

/**
 * checking segments within unconfirmed changes
 * if there are segments in states TK, UN, HX, US or UC an alert has to show up, This Journey has to be marked and
 * this segments needs to be removed because of problems during displaying them.
 * https://support.travelport.com/webhelp/uapi/Content/Air/Shared_Air_Topics/PNR_Status_Codes.htm
 * TK : Schedule Change. Advise passenger of new scheduled times.
 * HX : Cancel confirm hold
 * UN : Unable - no flight
 * US : Unable to sell
 * UC : Unable to confirm or waitlist
 */
const processUnconfirmedSegments = (booking: TFF.FlightDetails): TFF.FlightDetails => {
  const stateCodes: { [key: string]: string } = {
    TK: 'Schedule Change. Advise passenger of new scheduled times.',
    HX: 'Cancel confirm hold',
    UN: 'Unable - no flight',
    US: 'Unable to sell',
    UC: 'Unable to confirm or waitlist',
  };

  const unconfirmedStates = ['TK', 'HX', 'US', 'UC', 'UN'];
  const statesToIgnoreSegments = ['XYZ'];

  return {
    ...booking,
    OrderDetails: {
      ...booking.OrderDetails,
      Journeys: booking.OrderDetails.Journeys.map(j => {
        const newAdditionalParams = { ...j.AdditionalParams };
        const filteredSegments = [];

        j.Segments.forEach(segment => {
          if (segment.State && unconfirmedStates.includes(segment.State)) {
            newAdditionalParams[
              `TFF_ALERT:UNCONFIRMED_STATE:${segment.State}`
            ] = `SEGMENT_WITH_UNCONFIRMED_STATE:${stateCodes}:${stateCodes[segment.State]}`;

            if (!statesToIgnoreSegments.includes(segment.State)) {
              filteredSegments.push(segment);
            }
          } else {
            filteredSegments.push(segment);
          }
        });

        return {
          ...j,
          AdditionalParams: newAdditionalParams,
          Segments: filteredSegments,
        };
      }),
    },
  };
};

export const useRetrieveBooking = (retrieveBookingProps?: RetrieveBookingProps): RetrieveBookingResponse => {
  const [status, setStatus] = React.useState<TypeOfStatus | undefined>(undefined);
  const [fetchedBooking, setFetchedBooking] = React.useState<TFF.FlightDetails>();
  const [error, setError] = React.useState<string | RetrievePermissionError | undefined>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [market, setMarket] = React.useState<MarketType>();

  const { showLoading, closeLoading } = useContext(LoadingContext);
  const queryClient = useQueryClient();

  const loadBookingFromApigee = async (
    source: string,
    recordLocator: string,
    url: string,
  ): Promise<AxiosResponse<RetrieveFlightResponse | AwsLambdaErrorResponse>> => {
    setLoading(true);
    const dataKey = `get:booking:${source}:${recordLocator}`;
    const response: AxiosResponse<RetrieveFlightResponse | AwsLambdaErrorResponse> = await queryClient.fetchQuery(
      dataKey,
      async () => {
        return await apigeeEndpoint.get<RetrieveFlightResponse | AwsLambdaErrorResponse>(url);
      },
      {
        retry: 2,
        // staleTime: 5000,
        cacheTime: 10000,
        initialData: () => {
          return queryClient.getQueryData(dataKey);
        },
      },
    );
    setLoading(false);
    return response;
  };

  const resetRetrieveBookingState = (): void => {
    setFetchedBooking(undefined);
    setStatus(undefined);
    setError(undefined);
  };

  const isAwsLambdaError = (data: RetrieveFlightResponse | AwsLambdaErrorResponse): data is AwsLambdaErrorResponse => {
    return 'errorMessage' in data;
  };

  /** From NdcConnectorError we can get an Array response with type:
   *  export interface NdcConnectorError {
   *  readonly Code: string;
   *  readonly DescText?: string;
   *  readonly StatusText?: string;
   } **/
  const getErrorInResponse = (data: RetrieveFlightResponse): string => {
    if (data.Status !== 'FAILURE') {
      return UNKNOWN_ERROR_MESSAGE;
    }
    
    const error = data.Error as any;
    
    if (!error) {
      return UNKNOWN_ERROR_MESSAGE;
    }
    
    if (typeof error === 'string') {
      return error || UNKNOWN_ERROR_MESSAGE;
    }
    
    if (Array.isArray(error)) {
      if (error.length === 0) {
        return UNKNOWN_ERROR_MESSAGE;
      }
      const errorMessages = error
        .map((e: any) => e.DescText)
        .filter((desc: string) => desc && desc.trim() !== '')
        .join(', ');
      return errorMessages || UNKNOWN_ERROR_MESSAGE;
    }
    
    if (typeof error === 'object' && error !== null && Object.keys(error).length === 0) {
      return UNKNOWN_ERROR_MESSAGE;
    }
    
    return UNKNOWN_ERROR_MESSAGE;
  };

  useEffect(() => {
    const fetchDataFromBackend = async (recordLocator: string, sourceConfig: ISourceConfig): Promise<void> => {
      try {
        showLoading(`Fetch Booking from source ${sourceConfig.ApiPath}`);
        setStatus('IN_FETCHING');

        const url = `/retrieve/${sourceConfig.ApiPath}/${recordLocator}`;

        const response = await loadBookingFromApigee(sourceConfig.SourceGroup, recordLocator, url);

        if (isAwsLambdaError(response.data)) {
          setStatus('FAILURE');
          const errorMessage = response.data.errorMessage;
          if (!errorMessage || (typeof errorMessage === 'object' && Object.keys(errorMessage).length === 0)) {
            setError(UNKNOWN_ERROR_MESSAGE);
          } else {
            setError(errorMessage);
          }
          return;
        }

        // TypeScript now knows response.data is RetrieveFlightLambdaResponse
        if (sourceConfig.SourceGroup === 'TUI_NDC') {
          if (response.data.Status === 'SUCCESS') {
            const processedBooking = processUnconfirmedSegments(response.data.Booking);
            if (processedBooking) {
              const preparedBooking = CreateJourneyExtras(processedBooking);
              const marketAccess = checkMarketAccess(
                retrieveBookingProps?.sourceConfig?.BookingSource,
                preparedBooking.OrderSummary?.SourceDetails?.OfficeId,
              );
              if ('permissionError' in marketAccess) {
                setStatus('FAILURE');
                setError(marketAccess);
              } else {
                setStatus('FETCHED');
                setFetchedBooking(preparedBooking);
                setMarket(marketAccess.market);
              }
            }
          } else {
            setStatus('FAILURE');
            setError(getErrorInResponse(response.data));
          }
        } else {
          /** NSK and NDCX case **/
          if ((response.data as any).Error) {
            setStatus('FAILURE');
            setError((response.data as any).Error);
          } else if (response.data.Status === 'FAILURE') {
            setStatus('FAILURE');
            setError(getErrorInResponse(response.data));
          } else if (response.data.Status === 'SUCCESS') {
            const sourceDetails = response.data.Booking?.OrderSummary?.SourceDetails;
            const marketAccess = checkMarketAccess(
              retrieveBookingProps?.sourceConfig?.BookingSource,
              sourceDetails?.OfficeId,
              sourceDetails?.AgencyId,
              response.data.Booking?.OrderDetails?.Payments,
            );
            if ('permissionError' in marketAccess) {
              setStatus('FAILURE');
              setError(marketAccess);
            } else {
              setStatus('FETCHED');
              setFetchedBooking(response.data.Booking);
              setMarket(marketAccess.market);
            }
          }
        }
      } catch (error) {
        setError(error);
        setStatus('FAILURE');
      } finally {
        closeLoading();
      }
    };

    if (retrieveBookingProps && retrieveBookingProps.recordLocator && retrieveBookingProps.sourceConfig && !status) {
      const accessError = checkSourceAccess(retrieveBookingProps.sourceConfig.BookingSource);
      if ('permissionError' in accessError) {
        setStatus('FAILURE');
        setError(accessError);
      } else {
        void fetchDataFromBackend(retrieveBookingProps.recordLocator, retrieveBookingProps.sourceConfig);
      }
    }
  }, [retrieveBookingProps]);

  return { error, fetchedBooking, market, status, resetRetrieveBookingState, retrieveLoading: loading };
};
